import { AppInstallation, Image } from '@tribeplatform/gql-client/types'
import { useI18n, T } from '@tribeplatform/react-components/i18n'
import { useUninstallApp } from '@tribeplatform/react-sdk/hooks'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { ConnectedAppSections } from './ConnectedAppSections.js'

type AppInstallationComponentPropsTypes = {
  appInstallation: AppInstallation
}

export const ConnectedApp = ({
  appInstallation,
}: AppInstallationComponentPropsTypes) => {
  const { $t } = useI18n()

  const { mutateAsync: uninstallApp, isLoading: isUninstalling } =
    useUninstallApp()

  const onUninstallApp = () => {
    uninstallApp({ appInstallationId: appInstallation.id, reason: '' })
      .then(() => {
        toast({
          title: $t(
            {
              id: 'Settings.ConnectedApps.AppInstallationUninstalled',
              defaultMessage:
                '{app_installation_name} has been successfully uninstalled',
            },
            {
              app_installation_name: appInstallation?.app?.name || '',
            },
          ),
          status: 'success',
        })
      })
      .catch(() => {
        toast({
          title: $t(
            {
              id: 'Settings.ConnectedApps.AppUnableUninstall',
              defaultMessage: 'Unable to uninstall {app_installation_name}',
            },
            {
              app_installation_name: appInstallation?.app?.name || '',
            },
          ),
          status: 'error',
        })
      })
  }

  if (!appInstallation) {
    return null
  }

  return (
    <Accordion
      defaultOpen={false}
      defaultChecked={false}
      iconAlign="trailing"
      variant="plus"
      inset="inset"
    >
      <Accordion.Button>
        <div className="flex items-center ">
          <Avatar
            size="3x"
            rounded="md"
            name={appInstallation?.app?.name}
            src={(appInstallation?.app?.image as Image)?.url}
          />
          <p className="ps-2 text-sm font-semibold text-content-on-background-subdued">
            {appInstallation?.app?.name}
          </p>
        </div>
      </Accordion.Button>
      <Accordion.Panel>
        <ConnectedAppSections appInstallation={appInstallation} />
        <div className="flex space-s-5 justify-center">
          <Button
            fullWidth
            onClick={onUninstallApp}
            variant="primary"
            type="button"
            size="lg"
            loading={isUninstalling}
            disabled={isUninstalling}
          >
            <T
              defaultMessage="Uninstall app"
              id="Settings.ConnectedApps.UninstallApp"
            />
          </Button>
        </div>
      </Accordion.Panel>
    </Accordion>
  )
}
